import { useQuery } from "@tanstack/react-query";
import { AlertTriangle } from "lucide-react";
import type { HealthResponse } from "@pinpoint/shared";
import { apiFetch } from "@/lib/api";

/**
 * A standing banner on anything that isn't production.
 *
 * Staging looks identical to production and talks to the same Instantly
 * account through whatever agent token someone mints there — so an operator
 * who loses track of which tab is which can approve a sequence that reaches a
 * real prospect. This is the cheapest possible guard against that, and it is
 * deliberately hard to miss rather than tasteful.
 *
 * Unauthenticated, so it shows on the sign-in screen too — which is exactly
 * when someone is most likely to be looking at the wrong tab.
 */
export function EnvironmentBanner() {
  const health = useQuery({
    queryKey: ["health"],
    queryFn: () => apiFetch<HealthResponse>("/api/health"),
    staleTime: Infinity,
    retry: false,
  });

  const environment = health.data?.version;
  if (!environment || environment === "production") return null;

  return (
    <div className="flex shrink-0 items-center justify-center gap-2 bg-amber-500/90 px-4 py-1 text-center text-xs font-medium text-amber-950">
      <AlertTriangle className="size-3.5 shrink-0" />
      <span>
        {environment.toUpperCase()} — not production. Anything sent from here
        reaches real people.
      </span>
    </div>
  );
}
